// 👤 신청자 정보 섹션 컴포넌트
import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Stack,
  Avatar,
  Tooltip,
  Button,
  Grid,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Edit as EditIcon,
  LocalShipping as ShippingIcon,
  Launch as LaunchIcon,
  Business as BusinessIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import type { Application } from '../../../../types/application';
import type { CopyField } from '../types/applicationStickyHeader.types';

interface ApplicationInfoSectionProps {
  application: Application;
  copiedField: CopyField | null;
  getCopyIconStyles: (field: CopyField, size?: number) => object;
  getCopyTooltipTitle: (field: CopyField) => string;
  onCopy: (value: string, field: CopyField) => Promise<void>;
  onOpenTrackingDialog: (type: 'delivery' | 'return') => void;
}

/**
 * 애플리케이션 신청자 정보 섹션 컴포넌트
 * - 신청자 기본 정보 (이름, 전화번호)
 * - 수거 주소 정보
 * - 택배송장 정보 (발송/반품)
 */
export const ApplicationInfoSection: React.FC<ApplicationInfoSectionProps> = ({
  application,
  copiedField,
  getCopyIconStyles,
  getCopyTooltipTitle,
  onCopy,
  onOpenTrackingDialog,
}) => {
  return (
    <Grid item xs={12} md={4}>
      <Box
        sx={{
          p: 2,
          bgcolor: 'rgba(25, 118, 210, 0.04)',
          borderRadius: 2,
          border: '1px solid rgba(25, 118, 210, 0.1)',
          height: '100%',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <Avatar
            sx={{
              bgcolor: 'primary.main',
              width: 48,
              height: 48,
              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
            }}
          >
            <PersonIcon />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="h6" fontWeight={600}>
                {application.user_name}
              </Typography>
              {/* 🆕 수동 신청/거래처 배지 */}
              {(application.source_type === 'manual' || application.source_type === 'partner') && (
                <Chip
                  icon={<BusinessIcon sx={{ fontSize: 14 }} />}
                  label={application.source_type === 'partner' ? '거래처' : '수동'}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: 11,
                    fontWeight: 700,
                    bgcolor: application.source_type === 'partner' ? '#7c3aed' : '#6366f1',
                    color: '#fff',
                    '& .MuiChip-icon': { color: '#fff' },
                  }}
                />
              )}
              {/* 🆕 정보 미완성 배지 */}
              {application.is_incomplete && (
                <Chip
                  icon={<WarningIcon sx={{ fontSize: 14 }} />}
                  label="미완성"
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: 11,
                    fontWeight: 700,
                    bgcolor: '#f59e0b',
                    color: '#fff',
                    '& .MuiChip-icon': { color: '#fff' },
                  }}
                />
              )}
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {application.phone_number}
              </Typography>
            </Stack>
            {/* 🆕 거래처명 표시 */}
            {application.partner_info?.partner_name && (
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                <BusinessIcon sx={{ fontSize: 14, color: '#7c3aed' }} />
                <Typography variant="caption" sx={{ color: '#7c3aed', fontWeight: 600 }}>
                  {application.partner_info.partner_name}
                </Typography>
              </Stack>
            )}
          </Box>
        </Stack>

        {application.pickup_address && (
          <Stack direction="row" alignItems="flex-start" spacing={1}>
            <LocationIcon sx={{ fontSize: 16, color: 'text.secondary', mt: 0.2 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {application.pickup_address}
                {application.pickup_address_detail && ` ${application.pickup_address_detail}`}
              </Typography>
            </Box>
            {/* 📋 주소 복사 버튼 */}
            <Tooltip title={getCopyTooltipTitle('address')}>
              <IconButton
                size="small"
                onClick={() => {
                  const fullAddress = application.pickup_address_detail
                    ? `${application.pickup_address} ${application.pickup_address_detail}`
                    : application.pickup_address;
                  onCopy(fullAddress, 'address');
                }}
                sx={getCopyIconStyles('address', 28)}
              >
                {copiedField === 'address' ? (
                  <CheckIcon sx={{ fontSize: 14 }} />
                ) : (
                  <CopyIcon sx={{ fontSize: 14 }} />
                )}
              </IconButton>
            </Tooltip>
          </Stack>
        )}

        {/* 🚚 택배송장 정보 */}
        <Box sx={{ mt: 2 }}>
          <Stack spacing={1}>
            {/* 일반 택배송장 */}
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={1}>
                <ShippingIcon sx={{ fontSize: 16, color: '#1565c0' }} />
                <Typography variant="body2" fontWeight={600} color="text.primary">
                  발송 송장
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                {application.tracking_number ? (
                  <>
                    <Chip
                      label={application.tracking_number}
                      size="small"
                      sx={{
                        fontSize: '0.75rem',
                        height: 22,
                        bgcolor: '#e3f2fd',
                        color: '#1565c0',
                        fontWeight: 600,
                      }}
                    />
                    <Tooltip title="배송조회">
                      <IconButton
                        size="small"
                        onClick={() => window.open(`https://tracker.delivery/#/kr.cjlogistics/${application.tracking_number}`, '_blank')}
                        sx={{
                          width: 28,
                          height: 28,
                          color: '#1565c0',
                          '&:hover': { bgcolor: 'rgba(21, 101, 192, 0.04)' }
                        }}
                      >
                        <LaunchIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={getCopyTooltipTitle('delivery_tracking')}>
                      <IconButton
                        size="small"
                        onClick={() => onCopy(application.tracking_number || '', 'delivery_tracking')}
                        sx={getCopyIconStyles('delivery_tracking', 28)}
                      >
                        {copiedField === 'delivery_tracking' ? (
                          <CheckIcon sx={{ fontSize: 14 }} />
                        ) : (
                          <CopyIcon sx={{ fontSize: 14 }} />
                        )}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="수정">
                      <IconButton
                        size="small"
                        onClick={() => onOpenTrackingDialog('delivery')}
                        sx={{
                          width: 28,
                          height: 28,
                          color: '#757575',
                          '&:hover': { bgcolor: 'rgba(117, 117, 117, 0.04)' }
                        }}
                      >
                        <EditIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </>
                ) : (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => onOpenTrackingDialog('delivery')}
                    sx={{
                      fontSize: '0.75rem',
                      minWidth: 'auto',
                      px: 1.5,
                      py: 0.5,
                      height: 24,
                      borderColor: '#1565c0',
                      color: '#1565c0',
                      '&:hover': {
                        borderColor: '#0d47a1',
                        color: '#0d47a1',
                        bgcolor: 'rgba(21, 101, 192, 0.04)',
                      }
                    }}
                  >
                    입력
                  </Button>
                )}
              </Stack>
            </Stack>

                    {/* 반품 택배송장 (반송 상태이거나 이미 입력된 경우만 표시) */}
        {(application.status === '반송' || (application as any).return_tracking_number) && (
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <ShippingIcon sx={{ fontSize: 16, color: '#c62828' }} />
                  <Typography variant="body2" fontWeight={600} color="text.primary">
                    반품 송장
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  {(application as any).return_tracking_number ? (
                    <>
                      <Chip
                        label={(application as any).return_tracking_number}
                        size="small"
                        sx={{
                          fontSize: '0.75rem',
                          height: 22,
                          bgcolor: '#ffebee',
                          color: '#c62828',
                          fontWeight: 600,
                        }}
                      />
                      <Tooltip title="배송조회">
                        <IconButton
                          size="small"
                          onClick={() => window.open(`https://tracker.delivery/#/kr.cjlogistics/${(application as any).return_tracking_number}`, '_blank')}
                          sx={{
                            width: 28,
                            height: 28,
                            color: '#c62828',
                            '&:hover': { bgcolor: 'rgba(198, 40, 40, 0.04)' }
                          }}
                        >
                          <LaunchIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={getCopyTooltipTitle('return_tracking')}>
                        <IconButton
                          size="small"
                          onClick={() => onCopy((application as any).return_tracking_number || '', 'return_tracking')}
                          sx={getCopyIconStyles('return_tracking', 28)}
                        >
                          {copiedField === 'return_tracking' ? (
                            <CheckIcon sx={{ fontSize: 14 }} />
                          ) : (
                            <CopyIcon sx={{ fontSize: 14 }} />
                          )}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="수정">
                        <IconButton
                          size="small"
                          onClick={() => onOpenTrackingDialog('return')}
                          sx={{
                            width: 28,
                            height: 28,
                            color: '#757575',
                            '&:hover': { bgcolor: 'rgba(117, 117, 117, 0.04)' }
                          }}
                        >
                          <EditIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={() => onOpenTrackingDialog('return')}
                      sx={{
                        fontSize: '0.75rem',
                        minWidth: 'auto',
                        px: 1.5,
                        py: 0.5,
                        height: 24,
                        borderColor: '#c62828',
                        color: '#c62828',
                        '&:hover': {
                          borderColor: '#b71c1c',
                          color: '#b71c1c',
                          bgcolor: 'rgba(198, 40, 40, 0.04)',
                        }
                      }}
                    >
                      입력
                    </Button>
                  )}
                </Stack>
              </Stack>
            )}
          </Stack>
        </Box>
      </Box>
    </Grid>
  );
};